import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { computed } from '@angular/core';
import {
  INITIAL_STATE_FIELD,
  INIT_MODES,
  IReportTabState,
  InitMode,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  BunkerRowEntry,
  BunkerRowFormState,
  BunkerState,
  StockTankFormState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { StockTank } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { bunkerEvents } from './bunker-events';
import { buildBunkerVm } from './bunker-vm-builder';
import { withBunkerEffects } from './with-bunker-effects';

const initialBunkerStateReducer = (initialData: IReportTabState, initMode: InitMode) => {
  const data = initialData as BunkerState;

  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

const initWithExistingReport = (data: BunkerState) => {
  return {
    formState: {
      options: buildOptionsSection(data),
      debunkered: {
        startedDebunkering: initStateField<Date>(data.debunkered.startedDebunkering),
        finishedDebunkering: initStateField<Date>(data.debunkered.finishedDebunkering),
        debunkeredFuelGrade0: initStateField<SelectOption>(data.debunkered.debunkeredFuelGrade0),
        debunkeredQuantity0: initStateField<number>(data.debunkered.debunkeredQuantity0),
        debunkeredFuelGrade1: initStateField<SelectOption>(data.debunkered.debunkeredFuelGrade1),
        debunkeredQuantity1: initStateField<number>(data.debunkered.debunkeredQuantity1),
      },
      fuelOilBunkers: {
        startedBunkering: initStateField<Date>(data.fuelOilBunkers.startedBunkering),
        finishedBunkering: initStateField<Date>(data.fuelOilBunkers.finishedBunkering),
        bargeAlongside: initStateField<Date>(data.fuelOilBunkers.bargeAlongside),
        bargeAway: initStateField<Date>(data.fuelOilBunkers.bargeAway),
      },
      fuelOilBunkerTable: {
        bunkerRows: generateBunkerRowArrayFromExisting(data.fuelOilBunkerTable?.bunkerRows),
        quantityOrderedFreshWater: initStateField<number>(data.fuelOilBunkerTable.quantityOrderedFreshWater),
        quantityReceivedFreshWater: initStateField<number>(data.fuelOilBunkerTable.quantityReceivedFreshWater),
        bdnFiguresFreshWater: initStateField<string>(data.fuelOilBunkerTable.bdnFiguresFreshWater),
        quantityOrderedDistilledWater: initStateField<number>(data.fuelOilBunkerTable.quantityOrderedDistilledWater),
        quantityReceivedDistilledWater: initStateField<number>(data.fuelOilBunkerTable.quantityReceivedDistilledWater),
        bdnFiguresDistilledWater: initStateField<string>(data.fuelOilBunkerTable.bdnFiguresDistilledWater),
        protestIssued: initStateField<boolean>(data.fuelOilBunkerTable.protestIssued),
      },
      lubeOilBunkers: {
        startedBunkering: initStateField<Date>(data.lubeOilBunkers.startedBunkering),
        finishedBunkering: initStateField<Date>(data.lubeOilBunkers.finishedBunkering),
        meLubeOilSystemQuantity: initStateField<number>(data.lubeOilBunkers.meLubeOilSystemQuantity),
        meLubeOilSystemFigures: initStateField<string>(data.lubeOilBunkers.meLubeOilSystemFigures),
        meLubeOilSpareQuantity: initStateField<number>(data.lubeOilBunkers.meLubeOilSpareQuantity),
        meLubeOilSpareFigures: initStateField<string>(data.lubeOilBunkers.meLubeOilSpareFigures),
        lowBnCylinderOilQuantity: initStateField<number>(data.lubeOilBunkers.lowBnCylinderOilQuantity),
        lowBnCylinderOilFigures: initStateField<string>(data.lubeOilBunkers.lowBnCylinderOilFigures),
        highBnCylinderOilQuantity: initStateField<number>(data.lubeOilBunkers.highBnCylinderOilQuantity),
        highBnCylinderOilFigures: initStateField<string>(data.lubeOilBunkers.highBnCylinderOilFigures),
        auxiliaryEngineLubeOilQuantity: initStateField<number>(data.lubeOilBunkers.auxiliaryEngineLubeOilQuantity),
        auxiliaryEngineLubeOilFigures: initStateField<string>(data.lubeOilBunkers.auxiliaryEngineLubeOilFigures),
        turboOilQuantity: initStateField<number>(data.lubeOilBunkers.turboOilQuantity),
        turboOilFigures: initStateField<string>(data.lubeOilBunkers.turboOilFigures),
        hydraulicOilQuantity: initStateField<number>(data.lubeOilBunkers.hydraulicOilQuantity),
        hydraulicOilFigures: initStateField<string>(data.lubeOilBunkers.hydraulicOilFigures),
        otherOilQuantity: initStateField<number>(data.lubeOilBunkers.otherOilQuantity),
        otherOilFigures: initStateField<string>(data.lubeOilBunkers.otherOilFigures),
        protestIssued: initStateField<boolean>(data.lubeOilBunkers.protestIssued),
      },
      tankStockTable: {
        tanks: generateTankStockArrayFromExisting(data.tankStockTable?.tanks || {}),
      },
    },
  };
};

const generateBunkerRowArrayFromExisting = (existingRows: BunkerRowEntry[]) => {
  if (!existingRows) return [];
  const bunkerRowArray: BunkerRowFormState[] =
    existingRows?.map((row) => ({
      rowId: { value: uuidv4(), ...INITIAL_STATE_FIELD },
      fuelType: initStateField<SelectOption>(
        row.fuelType
          ? {
              name: row.fuelType.toLocaleUpperCase(),
              code: row.fuelType,
            }
          : undefined
      ),
      isoFuelGrade: initStateField<SelectOption>(
        row.isoFuelGrade
          ? {
              name: row.isoFuelGrade,
              code: row.isoFuelGrade,
            }
          : undefined
      ),
      quantityOrdered: initStateField<number>(row.quantityOrdered),
      quantityReceived: initStateField<number>(row.quantityReceived),
      bdnFigures: initStateField<string>(row.bdnFigures),
      density: initStateField<number>(row.density),
      lcv: initStateField<number>(row.lcv),
      sulphur: initStateField<number>(row.sulphur),
      viscosity: initStateField<number>(row.viscosity),
      labSulphur: initStateField<number>(row.labSulphur),
      stableUntil: initStateField<Date>(row.stableUntil),
    })) || [];

  return bunkerRowArray;
};

const generateTankStockArrayFromExisting = (stockTanks: Record<string, StockTank | undefined>) => {
  const tanks: StockTankFormState[] = [];

  Object.entries(stockTanks).forEach(([key, tankValue]) => {
    tanks.push(buildTankStockFormState(key, tankValue, tankValue?.viscosity));
  });

  return tanks;
};

const generateTankStockArray = (stockTanks: Record<string, StockTank | undefined>) => {
  const tanks: StockTankFormState[] = [];

  Object.entries(stockTanks).forEach(([key, tankValue]) => {
    tanks.push(buildTankStockFormState(key, tankValue, undefined));
  });

  return tanks;
};

const buildTankStockFormState = (
  key: string,
  tankValue: StockTank | undefined,
  viscosityValue: number | undefined
): StockTankFormState => ({
  id: initStateField<string>(key),
  amount: initStateField<number | undefined>(tankValue?.amount),
  amountBeforeBunkering: initStateField<number | undefined>(tankValue?.amountBeforeBunkering),
  bdnNumber: initStateField<string | undefined>(tankValue?.bdnNumber),
  bunkerDate: initStateField<Date | undefined>(tankValue?.bunkerDate),
  bunkerPort: initStateField<string | undefined>(tankValue?.bunkerPort),
  density: initStateField<number | undefined>(tankValue?.density),
  densityBeforeBunkering: initStateField<number | undefined>(tankValue?.densityBeforeBunkering),
  fuelClass: initStateField<string | undefined>(tankValue?.fuelClass),
  fuelGrade: initStateField<SelectOption | undefined>(tankValue?.fuelGrade),
  fuelGradeAddedInTank: initStateField<SelectOption | undefined>(tankValue?.fuelGradeAddedInTank),
  groupColor: initStateField<string | undefined>(undefined),
  inUse: initStateField<boolean | undefined>(tankValue?.inUse),
  isBlend: initStateField<boolean | undefined>(tankValue?.isBlend),
  index: initStateField<number | undefined>(undefined),
  lcv: initStateField<number | undefined>(tankValue?.lcv),
  lcvBeforeBunkering: initStateField<number | undefined>(tankValue?.lcvBeforeBunkering),
  stableUntil: initStateField<Date>(tankValue?.stableUntil),
  sulphurBeforeBunkering: initStateField<number | undefined>(tankValue?.sulphurBeforeBunkering),
  sulphurAfterBunkering: initStateField<number | undefined>(tankValue?.sulphurAfterBunkering),
  quantityAddedInTank: initStateField<number | undefined>(tankValue?.quantityAddedInTank),
  tankCapacity: initStateField<number | undefined>(undefined),
  tankName: initStateField<string | undefined>(undefined),
  viscosity: initStateField<number | undefined>(viscosityValue),
});

const generateBunkerRowArray = (existingRows: BunkerRowEntry[]) => {
  const bunkerRowArray: BunkerRowFormState[] =
    existingRows?.map(() => ({
      rowId: { value: uuidv4(), ...INITIAL_STATE_FIELD },
      fuelType: { value: undefined, ...INITIAL_STATE_FIELD },
      isoFuelGrade: { value: undefined, ...INITIAL_STATE_FIELD },
      quantityOrdered: { value: undefined, ...INITIAL_STATE_FIELD },
      quantityReceived: { value: undefined, ...INITIAL_STATE_FIELD },
      bdnFigures: { value: undefined, ...INITIAL_STATE_FIELD },
      density: { value: undefined, ...INITIAL_STATE_FIELD },
      lcv: { value: undefined, ...INITIAL_STATE_FIELD },
      sulphur: { value: undefined, ...INITIAL_STATE_FIELD },
      viscosity: { value: undefined, ...INITIAL_STATE_FIELD },
      labSulphur: { value: undefined, ...INITIAL_STATE_FIELD },
      stableUntil: initStateField<Date | undefined>(undefined),
    })) || [];

  return bunkerRowArray;
};

const initWithCarryOver = (data: BunkerState) => {
  return {
    formState: {
      options: buildOptionsSection(data),
      debunkered: {
        startedDebunkering: initStateField<undefined>(),
        finishedDebunkering: initStateField<undefined>(),
        // Empty, not an empty option: the rules count any option as a fuel type.
        debunkeredFuelGrade0: initStateField<SelectOption>(),
        debunkeredQuantity0: initStateField<undefined>(),
        debunkeredFuelGrade1: initStateField<SelectOption>(),
        debunkeredQuantity1: initStateField<undefined>(),
      },
      fuelOilBunkers: {
        // V1 starts both dates empty. A hidden section still sends its values.
        startedBunkering: initStateField<undefined>(),
        finishedBunkering: initStateField<undefined>(),
        bargeAlongside: initStateField<undefined>(),
        bargeAway: initStateField<undefined>(),
      },
      fuelOilBunkerTable: {
        bunkerRows: generateBunkerRowArray(data.fuelOilBunkerTable?.bunkerRows),
        quantityOrderedFreshWater: initStateField<undefined>(),
        quantityReceivedFreshWater: initStateField<undefined>(),
        bdnFiguresFreshWater: initStateField<undefined>(),
        quantityOrderedDistilledWater: initStateField<undefined>(),
        quantityReceivedDistilledWater: initStateField<undefined>(),
        bdnFiguresDistilledWater: initStateField<undefined>(),
        protestIssued: initStateField<undefined>(),
      },
      lubeOilBunkers: {
        startedBunkering: initStateField<undefined>(),
        finishedBunkering: initStateField<undefined>(),
        meLubeOilSystemQuantity: initStateField<undefined>(),
        meLubeOilSystemFigures: initStateField<undefined>(),
        meLubeOilSpareQuantity: initStateField<undefined>(),
        meLubeOilSpareFigures: initStateField<undefined>(),
        lowBnCylinderOilQuantity: initStateField<undefined>(),
        lowBnCylinderOilFigures: initStateField<undefined>(),
        highBnCylinderOilQuantity: initStateField<undefined>(),
        highBnCylinderOilFigures: initStateField<undefined>(),
        auxiliaryEngineLubeOilQuantity: initStateField<undefined>(),
        auxiliaryEngineLubeOilFigures: initStateField<undefined>(),
        turboOilQuantity: initStateField<undefined>(),
        turboOilFigures: initStateField<undefined>(),
        hydraulicOilQuantity: initStateField<undefined>(),
        hydraulicOilFigures: initStateField<undefined>(),
        otherOilQuantity: initStateField<undefined>(),
        otherOilFigures: initStateField<undefined>(),
        protestIssued: initStateField<undefined>(),
      },
      tankStockTable: {
        tanks: generateTankStockArray(data.tankStockTable?.tanks || {}),
      },
    },
  };
};

const initBlank = () => {
  return {
    formState: {
      options: {
        tookFuelOil: initStateField<boolean>(),
        tookLubeOil: initStateField<boolean>(),
        debunkered: initStateField<boolean>(),
      },
      debunkered: {
        startedDebunkering: initStateField<Date>(),
        finishedDebunkering: initStateField<Date>(),
        debunkeredFuelGrade0: initStateField<SelectOption>(),
        debunkeredQuantity0: initStateField<number>(),
        debunkeredFuelGrade1: initStateField<SelectOption>(),
        debunkeredQuantity1: initStateField<number>(),
      },
      fuelOilBunkers: {
        startedBunkering: initStateField<Date>(),
        finishedBunkering: initStateField<Date>(),
        bargeAlongside: initStateField<Date>(),
        bargeAway: initStateField<Date>(),
      },
      fuelOilBunkerTable: {
        bunkerRows: generateBunkerRowArray([{}]),
        quantityOrderedFreshWater: initStateField<number>(),
        quantityReceivedFreshWater: initStateField<number>(),
        bdnFiguresFreshWater: initStateField<string>(),
        quantityOrderedDistilledWater: initStateField<number>(),
        quantityReceivedDistilledWater: initStateField<number>(),
        bdnFiguresDistilledWater: initStateField<string>(),
        protestIssued: initStateField<boolean>(),
      },
      lubeOilBunkers: {
        startedBunkering: initStateField<Date>(),
        finishedBunkering: initStateField<Date>(),
        meLubeOilSystemQuantity: initStateField<number>(),
        meLubeOilSystemFigures: initStateField<string>(),
        meLubeOilSpareQuantity: initStateField<number>(),
        meLubeOilSpareFigures: initStateField<string>(),
        lowBnCylinderOilQuantity: initStateField<number>(),
        lowBnCylinderOilFigures: initStateField<string>(),
        highBnCylinderOilQuantity: initStateField<number>(),
        highBnCylinderOilFigures: initStateField<string>(),
        auxiliaryEngineLubeOilQuantity: initStateField<number>(),
        auxiliaryEngineLubeOilFigures: initStateField<string>(),
        turboOilQuantity: initStateField<number>(),
        turboOilFigures: initStateField<string>(),
        hydraulicOilQuantity: initStateField<number>(),
        hydraulicOilFigures: initStateField<string>(),
        otherOilQuantity: initStateField<number>(),
        otherOilFigures: initStateField<string>(),
        protestIssued: initStateField<boolean>(),
      },
      tankStockTable: {
        tanks: generateTankStockArray({}),
      },
    },
  };
};

export const BunkerStore = signalStore(
  { providedIn: 'root' },
  withDevtools('BunkerStore'),
  withState(initBlank()),
  withBunkerEffects(),
  withFormStoreReducer(bunkerEvents, initialBunkerStateReducer, 'bunker'),
  withComputed((store) => ({
    vm: computed(() => buildBunkerVm(store.formState())),
  }))
);

const buildOptionsSection = (data: BunkerState) => ({
  tookFuelOil: initStateField<boolean>(data.options.tookFuelOil),
  tookLubeOil: initStateField<boolean>(data.options.tookLubeOil),
  debunkered: initStateField<boolean>(data.options.debunkered),
});
